import { UserCheck, Users, HelpCircle, UserX, Mail, Bell, Check, Zap, Activity, BarChart3 } from 'lucide-react';
import { Button } from '../actions/Button';
import { RsvpBadge } from '../feedback/RsvpBadge';
import { Table, TableBody, TableRow, TableCell } from '../data/Table';
import { timeAgo } from '../../utils/timeAgo';
import type { RsvpSummary, GuestWithInvite } from '../../types';

interface RsvpStatsSectionProps {
  totals: RsvpSummary['totals'];
  recentResponses: RsvpSummary['recent_responses'];
  guests: GuestWithInvite[];
  onSendReminder: (guestIds: number[]) => void;
  onNudge: (guestIds: number[]) => void;
  onSendToUninvited: (guestIds: number[]) => void;
  onMarkAsInvited: (guestIds: number[]) => void;
  isSending: boolean;
  isAdmin: boolean;
  showInviteColumn?: boolean;
}

function StatCard({ value, label, subtitle, icon, color, dataBuilder }: { value: number; label: string; subtitle?: string; icon: React.ReactNode; color: string; dataBuilder?: string }) {
  return (
    <div className="bg-background-card rounded-xl p-3 sm:p-4 border border-border" data-builder={dataBuilder}>
      <div className="flex items-center gap-3 mb-1.5 sm:mb-2">
        <div className={`p-1.5 sm:p-2 rounded-lg ${color}`}>
          {icon}
        </div>
      </div>
      <div className="text-xl sm:text-2xl font-bold text-text-primary">{value}</div>
      <div className="text-xs sm:text-sm text-text-secondary">{label}</div>
      {subtitle && <div className="text-xs text-text-secondary mt-0.5 hidden sm:block">{subtitle}</div>}
    </div>
  );
}

function ResponseRateCard({ responded, total, className = '', dataBuilder }: { responded: number; total: number; className?: string; dataBuilder?: string }) {
  const pct = total > 0 ? Math.round((responded / total) * 100) : 0;

  return (
    <div className={`bg-background-card rounded-xl p-3 sm:p-4 border border-border ${className}`} data-builder={dataBuilder}>
      <div className="flex items-center gap-3 mb-1.5 sm:mb-2">
        <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10">
          <BarChart3 size={18} className="text-primary" />
        </div>
      </div>
      <div className="text-xl sm:text-2xl font-bold text-text-primary">
        {responded} <span className="text-sm sm:text-base font-normal text-text-secondary">/ {total}</span>
      </div>
      <div className="text-xs sm:text-sm text-text-secondary">Responded</div>
      <div className="mt-2 w-full h-1.5 rounded-full bg-border overflow-hidden">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}


function isEligibleForEmail(g: GuestWithInvite, isAdmin: boolean): boolean {
  if (!g.email || g.parent_guest_id) return false;
  if (g.unsubscribed_at) return false; // Skip unsubscribed guests
  if (isAdmin) return true;
  if (g.invite.status === 'draft') return false;
  if (g.email_sent_at) {
    const today = new Date(new Date().setHours(0, 0, 0, 0));
    if (new Date(g.email_sent_at) > today) return false;
  }
  return true;
}

export function RsvpStatsSection({ totals, recentResponses, guests, onSendReminder, onNudge, onSendToUninvited, onMarkAsInvited, isSending, isAdmin, showInviteColumn = true }: RsvpStatsSectionProps) {
  const awaitingReply = guests.filter(
    (g) => g.email_sent_at && g.rsvp_status === 'pending' && g.email && !g.parent_guest_id && isEligibleForEmail(g, isAdmin)
  );
  const viewedNotReplied = guests.filter(
    (g) => g.view_count > 0 && g.rsvp_status === 'pending' && g.email && !g.parent_guest_id && isEligibleForEmail(g, isAdmin)
  );
  const notInvited = guests.filter(
    (g) => !g.email_sent_at && !g.parent_guest_id && !g.rsvp_submitted_at
  );
  const notInvitedWithEmail = notInvited.filter((g) => g.email && isEligibleForEmail(g, isAdmin));

  return (
    <div className="space-y-4">
      {/* Stat cards */}
      <div>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
          <ResponseRateCard
            responded={guests.filter(g => g.rsvp_submitted_at && !g.parent_guest_id).length}
            total={guests.filter(g => !g.parent_guest_id).length}
            className="col-span-2 md:col-span-1"
            dataBuilder="guest-stat-response-rate"
          />
          <StatCard
            value={totals.attending}
            label="Attending"
            subtitle={totals.total_plus_ones > 0 ? `incl. ${totals.total_plus_ones} plus one${totals.total_plus_ones !== 1 ? 's' : ''}` : undefined}
            icon={<UserCheck size={18} className="text-success" />}
            color="bg-success/10"
            dataBuilder="guest-stat-attending"
          />
          <StatCard
            value={totals.pending}
            label="Awaiting"
            icon={<Users size={18} className="text-text-muted" />}
            color="bg-text-muted/10"
            dataBuilder="guest-stat-awaiting"
          />
          <StatCard
            value={totals.maybe}
            label="Maybe"
            icon={<HelpCircle size={18} className="text-warning" />}
            color="bg-warning/10"
            dataBuilder="guest-stat-maybe"
          />
          <StatCard
            value={totals.not_attending}
            label="Not Attending"
            icon={<UserX size={18} className="text-error" />}
            color="bg-error/10"
            dataBuilder="guest-stat-not-attending"
          />
        </div>
      </div>

      {/* Actions + Activity side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Actions card */}
        <div className="bg-background-card rounded-xl border border-border">
          <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-border flex items-center gap-2">
            <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10">
              <Zap size={16} className="text-primary sm:w-[18px] sm:h-[18px]" />
            </div>
            <h3 className="text-sm sm:text-base font-medium text-text-primary">Actions</h3>
          </div>
          <div className="p-3 sm:p-4">
            {awaitingReply.length === 0 && viewedNotReplied.length === 0 && notInvitedWithEmail.length === 0 ? (
              <p className="text-xs sm:text-sm text-text-secondary">No actions needed right now</p>
            ) : (
              <div className="space-y-2.5 sm:space-y-3">
                {notInvitedWithEmail.length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm text-text-secondary">
                      {notInvitedWithEmail.length} not invited
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="tertiary"
                        size="sm"
                        icon={<Mail size={14} />}
                        onClick={() => onSendToUninvited(notInvitedWithEmail.map((g) => g.id))}
                        disabled={isSending}
                        className="text-xs sm:text-sm"
                        data-builder="stats-send-uninvited-btn"
                      >
                        Send
                      </Button>
                      <Button
                        variant="tertiary"
                        size="sm"
                        icon={<Check size={14} />}
                        onClick={() => onMarkAsInvited(notInvited.map((g) => g.id))}
                        disabled={isSending}
                        className="text-xs sm:text-sm"
                        data-builder="stats-mark-invited-btn"
                      >
                        Mark as Invited
                      </Button>
                    </div>
                  </div>
                )}
                {awaitingReply.length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm text-text-secondary">
                      {awaitingReply.length} awaiting reply
                    </span>
                    <Button
                      variant="tertiary"
                      size="sm"
                      icon={<Mail size={14} />}
                      onClick={() => onSendReminder(awaitingReply.map((g) => g.id))}
                      disabled={isSending}
                      className="text-xs sm:text-sm"
                      data-builder="stats-send-reminder-btn"
                    >
                      Send Reminder
                    </Button>
                  </div>
                )}
                {viewedNotReplied.length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm text-text-secondary">
                      {viewedNotReplied.length} viewed, no RSVP
                    </span>
                    <Button
                      variant="tertiary"
                      size="sm"
                      icon={<Bell size={14} />}
                      onClick={() => onNudge(viewedNotReplied.map((g) => g.id))}
                      disabled={isSending}
                      className="text-xs sm:text-sm"
                      data-builder="stats-nudge-btn"
                    >
                      Nudge
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Activity card */}
        <div id="activity-widget" className="bg-background-card rounded-xl border border-border min-w-0 overflow-hidden">
          <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-border flex items-center gap-2">
            <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10">
              <Activity size={16} className="text-primary sm:w-[18px] sm:h-[18px]" />
            </div>
            <h3 className="text-sm sm:text-base font-medium text-text-primary">Activity</h3>
          </div>
          {recentResponses.length === 0 ? (
            <div className="p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-text-secondary">No responses yet</p>
            </div>
          ) : (
            <div className="max-h-[280px] overflow-y-auto">
              <Table>
                <TableBody>
                  {recentResponses.slice(0, 10).map((r) => (
                    <TableRow key={r.id} hoverable={false}>
                      <TableCell className="min-w-0 w-full">
                        <span className="font-medium block truncate">
                          {r.name}{r.plus_count > 0 && <span className="text-text-tertiary font-normal"> +{r.plus_count}</span>}
                        </span>
                        {showInviteColumn && (
                          <span className="text-xs text-text-tertiary block truncate">{r.invite_title}</span>
                        )}
                        <span className="text-xs text-text-tertiary whitespace-nowrap" suppressHydrationWarning>
                          {timeAgo(r.rsvp_submitted_at)}
                        </span>
                      </TableCell>
                      <TableCell className="w-px whitespace-nowrap">
                        <RsvpBadge status={r.rsvp_status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
